'use client';

import { Search, Megaphone, Smartphone, Users, Mail } from 'lucide-react';
import { FONT_TEXT } from '@/app/fonts';
import { ShareableChart } from '@/components/blog/ShareableChart';

const channels = [
  { name: 'SEO / Organic', Icon: Search, color: '#10b981' },
  { name: 'Google Ads', Icon: Megaphone, color: '#294bfd' },
  { name: 'Facebook / Meta', Icon: Smartphone, color: '#294bfd' },
  { name: 'Agent Referrals', Icon: Users, color: '#ff34a7' },
  { name: 'Database / Email', Icon: Mail, color: '#ff34a7' },
  { name: 'Social Media', Icon: Smartphone, color: '#294bfd' },
];

export function IntegratedFunnel() {
  return (
    <ShareableChart title="Integrated Lead Generation Ecosystem">
      <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
        The Integrated Lead Generation Ecosystem
      </h3>
      <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: FONT_TEXT }}>
        All channels feed one conversion engine — they reinforce each other
      </p>

      {/* Channel sources flowing into center */}
      <div className="flex flex-col items-center">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full mb-4">
          {channels.map((ch) => (
            <div 
              key={ch.name}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm"
              style={{ borderColor: ch.color, fontFamily: FONT_TEXT }}
            >
              <ch.Icon size={16} aria-hidden="true" className="shrink-0" />
              <span className="font-medium text-gray-800">{ch.name}</span>
            </div>
          ))}
        </div>

        {/* Arrows down */}
        <div className="flex items-center justify-center gap-2 my-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </div>

        {/* Conversion Engine */}
        <div className="w-full rounded-lg p-5 text-center border-2" style={{ borderColor: '#ff34a7', backgroundColor: '#fff5fb' }}>
          <h4 className="font-bold text-lg mb-2" style={{ fontFamily: FONT_TEXT, color: '#ff34a7' }}>
            Your Conversion Engine
          </h4>
          <p className="text-sm text-gray-600 mb-3" style={{ fontFamily: FONT_TEXT }}>
            High-converting website + landing pages + speed-to-lead systems
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-xl font-bold" style={{ color: '#ff34a7', fontFamily: FONT_TEXT }}>5-35%</div>
              <div className="text-xs text-gray-500">Visitor → Lead</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold" style={{ color: '#294bfd', fontFamily: FONT_TEXT }}>60%+</div>
              <div className="text-xs text-gray-500">Contact Rate</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold" style={{ color: '#10b981', fontFamily: FONT_TEXT }}>3-6%</div>
              <div className="text-xs text-gray-500">Lead → Funded</div>
            </div>
          </div>
        </div>

        {/* Arrow down */}
        <div className="my-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </div>

        {/* Result */}
        <div className="w-full rounded-lg p-4 text-center" style={{ backgroundColor: '#ecfdf5' }}>
          <div className="text-2xl font-bold mb-1" style={{ color: '#10b981', fontFamily: FONT_TEXT }}>
            Funded Loans + Brand Equity
          </div>
          <p className="text-sm text-gray-600" style={{ fontFamily: FONT_TEXT }}>
            Revenue you earn <em>and</em> an asset you own that grows over time
          </p>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-4" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Conversion ranges depend on channel, market, and optimization level
      </p>
    </ShareableChart>
  );
}
